import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Mail, Lock, User, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

/**
 * Mundalo Sign-in / Sign-up / Forgot Password / Set Password - light theme.
 *
 * Brand tokens enforced:
 *   • Page bg              Canvas      #F9F6EF
 *   • Card bg              White       #FFFFFF
 *   • Card border          Sand        #C4B9A0
 *   • Label / input text   Deep Ocean  #0A1F2E (Outfit 400)
 *   • Placeholder          #5A6B73 (Ocean muted)
 *   • Field border default Sand        #C4B9A0
 *   • Field border focus   Teal        #2CA8A0
 *   • Submit button bg     Golden Hour #E8A830
 *   • Submit button text   Deep Ocean  #0A1F2E (bold)
 *   • Forgot link          Teal        #2CA8A0 (underline on hover)
 *   • Error                Crimson     #C0392B (bold)
 *   • Success              Pacific     #1A5C6E
 */
const C = {
  canvas:  '#F9F6EF',
  navy:    '#0A1F2E',
  ocean:   '#5A6B73',
  sand:    '#C4B9A0',
  teal:    '#2CA8A0',
  gold:    '#E8A830',
  pacific: '#1A5C6E',
  error:   '#C0392B',
};

const labelCls   = 'block text-sm font-normal mb-2 text-[#0A1F2E]';
const inputBase  = 'w-full bg-white border text-[#0A1F2E] placeholder-[#5A6B73]/70 py-3 focus:outline-none transition';
const inputPad   = 'pl-12 pr-4';
const inputPadPw = 'pl-12 pr-12';
const inputBorder = 'border-[#C4B9A0] focus:border-[#2CA8A0]';
const inputCls    = `${inputBase} ${inputPad} ${inputBorder}`;
const inputPwCls  = `${inputBase} ${inputPadPw} ${inputBorder}`;
const submitCls  = 'w-full bg-[#E8A830] text-[#0A1F2E] py-3 font-bold tracking-wide hover:brightness-95 transition disabled:opacity-60 flex items-center justify-center gap-2';
const linkCls    = 'text-[#2CA8A0] hover:underline transition font-medium';

const PortalLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, signup, forgotPassword, user, loading: authLoading } = useAuth();

  // iter 85: only auto-redirect to the portal if the user previously
  // checked "Stay signed in". Without the flag we always render the login
  // form - this lets a user switch accounts without being instantly bounced
  // into the previous session's dashboard.
  useEffect(() => {
    if (authLoading || !user) return;
    let remembered = false;
    try {
      remembered = typeof window !== 'undefined' &&
        window.localStorage.getItem('mundalo_remember_me') === '1';
    } catch (_) { remembered = false; }
    if (remembered) navigate('/portal/dashboard', { replace: true });
  }, [authLoading, user, navigate]);

  // iter 143 - magic-link sign-in. When a tool email link is followed the
  // URL carries `?mlt=<token>`. We exchange the token for a JWT cookie via
  // the backend, then drop the user into their portal dashboard. If the
  // token has expired or already been used we surface a friendly message.
  const [magicLoading, setMagicLoading] = useState(
    !!searchParams.get('mlt')
  );
  useEffect(() => {
    const token = searchParams.get('mlt');
    if (!token) { setMagicLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/magic-login?token=${encodeURIComponent(token)}`,
          { credentials: 'include' },
        );
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.ok && data.success) {
          try { window.localStorage.setItem('mundalo_remember_me', '1'); } catch (_) { /* noop */ }
          navigate('/portal/dashboard', { replace: true });
        } else {
          setError(data.detail || 'This magic link has expired. Visit mundalo.com/start to get a fresh one.');
          setMagicLoading(false);
        }
      } catch (_) {
        if (!cancelled) {
          setError('Could not validate your magic link. Please try again or visit /start.');
          setMagicLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, navigate]);

  const [mode, setMode] = useState('login'); // login | signup | forgot | setpassword | recover
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // iter 71 - required signup consents
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);
  // iter 85 - "Stay signed in" checkbox; unchecked by default so the
  // browser drops the auth cookie when the tab closes.
  const [rememberMe, setRememberMe] = useState(false);
  // iter 185 - Recovery for paid users who never set their password
  // (closed the success tab before clicking "Start Your Blueprint").
  // When the backend login endpoint returns { needs_password: true } we
  // land in recover mode with a pre-filled email. The nav "Already
  // purchased?" link also lands here via ?recover=1.
  const [recoverySent, setRecoverySent] = useState(false);
  useEffect(() => {
    if (searchParams.get('recover') === '1') {
      setMode('recover');
    }
  }, [searchParams]);

  // iter 185.c - Proactive "just paid" nudge. Shown ABOVE the login
  // form (not inside it, not blocking it) whenever the user arrives on
  // /portal from the Blueprint Success page. Two triggers, either works:
  //   1. ?just_paid=1 query param - safest, survives address-bar
  //      pastes and browser cross-tab drops of document.referrer.
  //   2. document.referrer contains /relocation/blueprint/success -
  //      catches the "closed the success tab and clicked Sign In from
  //      the header" same-session case where no explicit param exists.
  // Purely additive - login/signup/forgot/recover UX untouched.
  const [justPaidHint, setJustPaidHint] = useState(false);
  useEffect(() => {
    if (searchParams.get('just_paid') === '1') {
      setJustPaidHint(true);
      return;
    }
    try {
      const ref = (typeof document !== 'undefined' && document.referrer) || '';
      if (ref.includes('/relocation/blueprint/success')) {
        setJustPaidHint(true);
      }
    } catch (_) { /* referrer access can throw in sandboxed contexts */ }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const result = await login(email, password, rememberMe);
      if (result.needsPassword) {
        // iter 185 - Paid user who never set a password. Instead of the
        // old (broken) setpassword mode, flip to the dedicated recovery
        // flow with a pre-filled email + a "resend setup link" button.
        setEmail(result.email || email);
        setMode('recover');
        setError('');
        setSuccess('');
        setRecoverySent(false);
      } else if (result.success) navigate('/portal/dashboard');
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };
  const handleResendPortalAccess = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-portal-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data.detail || 'Could not send the setup link. Please try again.');
      } else {
        setSuccess(data.message || 'Setup link sent - check your inbox.');
        setRecoverySent(true);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally { setLoading(false); }
  };
  const handleSignup = async (e) => {
    e.preventDefault(); setError('');
    if (password !== confirmPassword) return setError('Passwords do not match');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (!termsAccepted) return setError('You must accept the Terms of Service to continue.');
    if (!emailConsent) return setError('Please confirm you agree to receive Mundalo emails to continue.');
    setLoading(true);
    try {
      const result = await signup(email, password, confirmPassword, firstName, lastName, {
        terms_accepted: termsAccepted,
        email_consent: emailConsent,
      });
      if (result.success) {
        setSuccess(result.message);
        setMode('login');
        setPassword(''); setConfirmPassword('');
      }
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };
  const handleForgotPassword = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const result = await forgotPassword(email);
      setSuccess(result.message);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
      <div>
        <label className={labelCls}>Email</label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
          <input
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="you@example.com"
            data-testid="login-email"
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>Password</label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
          <input
            type={showPassword ? 'text' : 'password'} required value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputPwCls}
            placeholder="••••••••"
            data-testid="login-password"
          />
          <button type="button" onClick={() => setShowPassword((v) => !v)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A6B73] hover:text-[#0A1F2E]"
            aria-label="Toggle password visibility">
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <button type="submit" disabled={loading} className={submitCls} data-testid="login-submit">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        {loading ? 'Signing in…' : 'Sign In'}
      </button>
      {/* iter 85 - "Stay signed in" checkbox; unchecked by default. */}
      <label
        className="flex items-center gap-2 text-sm text-[#0A1F2E]/85 cursor-pointer select-none"
        data-testid="login-stay-signed-in-row"
      >
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className="w-4 h-4 rounded border-[#0A1F2E]/30 text-[#2CA8A0] focus:ring-[#2CA8A0]/40"
          data-testid="login-stay-signed-in-checkbox"
        />
        Stay signed in on this device
      </label>
      <div className="flex justify-between text-sm">
        <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
          className={linkCls} data-testid="link-forgot-password">
          Forgot password?
        </button>
        <Link to="/start-trial" className={linkCls} data-testid="link-create-account">
          Create account
        </Link>
      </div>
      {/* iter 185 - Recovery entry point for paid users who never set a
          password. Small, discreet - visible enough that someone who
          vaguely remembers buying can find it without searching old
          emails. Also reachable via the header nav "Already purchased?"
          link (which lands on this page with ?recover=1). */}
      <div className="text-center text-sm pt-1">
        <button
          type="button"
          onClick={() => { setMode('recover'); setError(''); setSuccess(''); setRecoverySent(false); }}
          className={`${linkCls} text-[13px]`}
          data-testid="link-recover-paid-account"
        >
          Already paid but never set up your account?
        </button>
      </div>
    </form>
  );

  // iter 185 - Recovery form for a paid user who never set their
  // password. Triggered either by (a) a failed login that returned
  // needs_password_set: true, or (b) direct entry via /portal?recover=1
  // (nav link "Already purchased?"). Calls /api/auth/resend-portal-access
  // which is idempotent + rate-limited + non-enumerating.
  const renderRecoverForm = () => (
    <form onSubmit={handleResendPortalAccess} className="space-y-5" data-testid="recover-form">
      <div
        className="p-4 bg-[#2CA8A0]/10 border border-[#2CA8A0]/30 rounded text-sm text-[#0A1F2E] leading-relaxed"
        data-testid="recover-explainer"
      >
        <p className="font-semibold text-[#1A5C6E] mb-1">You already have an account.</p>
        <p>
          Looks like you purchased but never finished setting up your password. We&apos;ll email you a fresh setup link - one click to open your portal.
        </p>
      </div>
      <div>
        <label className={labelCls}>Email you used at checkout</label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
          <input
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="you@example.com"
            data-testid="recover-email"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={loading || recoverySent}
        className={submitCls}
        data-testid="recover-submit"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        {loading ? 'Sending…' : recoverySent ? 'Setup link sent ✓' : 'Send me the setup link'}
      </button>
      <div className="text-center text-sm">
        <button
          type="button"
          onClick={() => { setMode('login'); setError(''); setSuccess(''); setRecoverySent(false); }}
          className={linkCls}
          data-testid="recover-back-to-login"
        >
          Back to sign in
        </button>
      </div>
    </form>
  );

  const renderSignupForm = () => (
    <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>First Name</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
            <input type="text" required value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={inputCls} placeholder="Jamie" data-testid="signup-firstname" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Last Name</label>
          <input type="text" required value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={`${inputBase} px-4 ${inputBorder}`} placeholder="Rivera"
            data-testid="signup-lastname" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Email</label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
          <input type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls} placeholder="you@example.com" data-testid="signup-email" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Password</label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
          <input type={showPassword ? 'text' : 'password'} required minLength={8} value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputPwCls} placeholder="Min 8 characters" data-testid="signup-password" />
          <button type="button" onClick={() => setShowPassword((v) => !v)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A6B73] hover:text-[#0A1F2E]"
            aria-label="Toggle password visibility">
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <div>
        <label className={labelCls}>Confirm Password</label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
          <input type={showPassword ? 'text' : 'password'} required value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={inputCls} placeholder="••••••••" data-testid="signup-confirm" />
        </div>
      </div>
      {/* Iter 71 - required signup consents */}
      <div className="space-y-3 pt-1" data-testid="signup-consent-block">
        <label className="flex items-start gap-3 text-[13px] text-[#0A1F2E]/85 leading-snug cursor-pointer">
          <input
            type="checkbox"
            required
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-[#0A1F2E]/30 text-[#2CA8A0] focus:ring-[#2CA8A0]/40 flex-shrink-0"
            data-testid="signup-terms-checkbox"
          />
          <span>
            I have read and agree to the{' '}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1A5C6E] underline hover:text-[#0A1F2E]"
              data-testid="signup-terms-link"
            >
              Terms of Service
            </Link>.
          </span>
        </label>
        <label className="flex items-start gap-3 text-[13px] text-[#0A1F2E]/85 leading-snug cursor-pointer">
          <input
            type="checkbox"
            required
            checked={emailConsent}
            onChange={(e) => setEmailConsent(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-[#0A1F2E]/30 text-[#2CA8A0] focus:ring-[#2CA8A0]/40 flex-shrink-0"
            data-testid="signup-email-consent-checkbox"
          />
          <span>
            I agree to receive emails from Mundalo including my Blueprint,
            coaching confirmations, and community updates. I can unsubscribe at
            any time.
          </span>
        </label>
      </div>
      <button type="submit" disabled={loading} className={submitCls} data-testid="signup-submit">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        {loading ? 'Creating account…' : 'Create Account'}
      </button>
      <div className="text-center">
        <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
          className={`text-sm ${linkCls}`}>Already have an account? Sign in</button>
      </div>
    </form>
  );

  const renderForgotForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-5" data-testid="forgot-form">
      <p className="text-[#5A6B73] text-sm mb-2">
        Enter your email address and we’ll send you a link to reset your password.
      </p>
      <div>
        <label className={labelCls}>Email</label>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6B73]" />
          <input type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls} placeholder="you@example.com" data-testid="forgot-email" />
        </div>
      </div>
      <button type="submit" disabled={loading} className={submitCls} data-testid="forgot-submit">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        {loading ? 'Sending…' : 'Send Reset Link'}
      </button>
      <div className="text-center">
        <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
          className={`text-sm ${linkCls}`}>Back to sign in</button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-[#F9F6EF]" data-testid="portal-login-root">
      <Header currentPage="portal" />
      {magicLoading ? (
        <section className="pt-40 pb-32 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#2CA8A0] mx-auto mb-4" />
          <p className="font-mono uppercase text-[10px] tracking-[0.22em] text-[#1A5C6E] mb-2">
            Magic link
          </p>
          <p className="text-[#0A1F2E]">Signing you into your portal…</p>
        </section>
      ) : (
      <section className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border border-[#C4B9A0] rounded-2xl p-8 shadow-sm">
            <div className="text-center mb-8">
              <img src="https://res.cloudinary.com/des9fk8et/image/upload/v1781298749/mundalo/brand/mundalo-logo-black.png" alt="Mundalo" className="block h-10 w-[200px] object-contain object-left" />
              <p className="font-mono uppercase text-[10px] tracking-[0.22em] text-[#1A5C6E] mt-1">
                Move abroad · live freely
              </p>
              <h1 className="text-2xl text-[#0A1F2E] font-light mt-5">
                {mode === 'login' && 'Sign In'}
                {mode === 'signup' && 'Create Account'}
                {mode === 'forgot' && 'Reset Password'}
                {mode === 'setpassword' && 'Set Your Password'}
                {mode === 'recover' && 'Finish Account Setup'}
              </h1>
            </div>

            {mode === 'login' && justPaidHint && (
              <div
                className="mb-4 p-3 bg-[#E8A830]/12 border border-[#E8A830]/45 rounded text-sm text-[#0A1F2E] leading-relaxed flex items-start gap-2"
                data-testid="just-paid-hint"
              >
                <Mail className="w-4 h-4 mt-0.5 text-[#a8511f] flex-shrink-0" />
                <span>
                  <strong>Just paid?</strong> We just emailed your setup link - check your inbox first.
                </span>
              </div>
            )}

            {mode === 'login' && (
              <p className="text-[#5A6B73] text-sm text-center mb-6 bg-[#F9F6EF] p-3 border border-[#C4B9A0]/70 rounded">
                Sign in with the same email you used at checkout to access your Blueprint, assistant, and relocation tracker.
              </p>
            )}

            {error && (
              <div
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm flex items-center gap-2 font-semibold"
                style={{ color: C.error }}
                data-testid="login-error"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div
                className="mb-4 p-3 bg-[#2CA8A0]/10 border border-[#2CA8A0]/30 rounded text-sm flex items-center gap-2"
                style={{ color: C.pacific }}
                data-testid="login-success"
              >
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                {success}
              </div>
            )}

            {mode === 'login' && renderLoginForm()}
            {mode === 'signup' && renderSignupForm()}
            {mode === 'forgot' && renderForgotForm()}
            {mode === 'recover' && renderRecoverForm()}

            <p className="text-[#5A6B73] text-xs text-center mt-6">
              By signing in, you agree to our terms of service and privacy policy.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {[
              ['AI Blueprint', 'Your personalized relocation plan'],
              ['Chatbot', 'Ask anything about your move'],
              ['Journey Tracker', 'Track your 5-phase progress'],
              ['Resources', 'Vetted tools and partners'],
            ].map(([title, desc]) => (
              <div key={title} className="bg-white border border-[#C4B9A0] rounded-lg p-4">
                <p className="text-[#0A1F2E] text-sm font-medium mb-1">{title}</p>
                <p className="text-[#5A6B73] text-xs">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}
      <Footer />
    </div>
  );
};

export default PortalLogin;
